import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { fabric } from 'fabric';
import { message } from "antd";
import styles from "./index.module.scss"
import * as _ from "underscore";
import eventBus from '@/utils/eventBus';
import { forwardRef, useImperativeHandle } from "react"

const LabelBox2 = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        handleUndo,
        handleReset,
        handleAddImage,
    }))
    const fabricObjParams = {
        fill: "transparent",
        perPixelTargetFind: true,
        transparentCorners: false, // 透明控制角
        cornerColor: "#3572FF", // 控制角颜色
        cornerStrokeColor: "#FFFFFF", // 控制角边框颜色
        cornerSize: 10, // 控制角大小
        cornerStyle: "circle", // 控制角形状
        borderScaleFactor: 2, // 辅助边粗细
        borderColor: '#3572FF', // 辅助边颜色
        borderOpacityWhenMoving: .2
    }
    const isMountedRef = useRef(true)

    // 编辑模式
    const stroke = 'rgba(255, 0, 0, 1)'
    const strokeAlpha = 'rgba(255, 0, 0, .3)'
    // 编辑模式
    const modeRef = useRef(props.mode)
    useEffect(() => {
        modeRef.current = props.mode
    }, [props.mode])
    // 画笔宽度
    const strokeWidthRef = useRef()
    const strokeWidth = useMemo(() => {
        return (props.width / 512) * 4
    }, [props.width])
    useEffect(() => {
        strokeWidthRef.current = strokeWidth
        if (fabricInstanceRef.current) fabricInstanceRef.current.freeDrawingBrush.width = strokeWidthRef.current
    }, [strokeWidth])
    // 字体
    const fontSizeRef = useRef()
    const fontSize = useMemo(() => {
        return (props.width / 512) * 24
    }, [props.width])
    useEffect(() => {
        fontSizeRef.current = fontSize
    }, [fontSize])

    // 绘制矩形
    const downPointRef = useRef()
    const upPointRef = useRef()
    const currentRectRef = useRef()
    const handleMouseDownRect = ev => {
        const downPoint = ev.absolutePointer
        const rect = new fabric.Rect({
            left: downPoint.x,
            top: downPoint.y,
            width: 0,
            height: 0,
            stroke: strokeAlpha,
            strokeWidth: strokeWidthRef.current,
            ...fabricObjParams,
        })
        addDefaultControlVisibility(rect)
        currentRectRef.current = rect
        downPointRef.current = downPoint
        fabricInstanceRef.current.add(currentRectRef.current)
    }
    const handleMouseMoveRect = ev => {
        if (!downPointRef.current) return
        if (!currentRectRef.current) return
        const currentPoint = ev.absolutePointer
        const downPoint = downPointRef.current
        currentRectRef.current.set({
            left: Math.min(downPoint.x, currentPoint.x),
            top: Math.min(downPoint.y, currentPoint.y),
            width: Math.abs(downPoint.x - currentPoint.x),
            height: Math.abs(downPoint.y - currentPoint.y),
        })
        fabricInstanceRef.current.requestRenderAll()
    }
    const handleMouseUpRect = ev => {
        if (!downPointRef.current) return
        if (!currentRectRef.current) return
        const upPoint = ev.absolutePointer
        const downPoint = downPointRef.current
        if (JSON.stringify(downPoint) === JSON.stringify(upPoint)) {
            fabricInstanceRef.current.remove(currentRectRef.current)
            // 更新标注
            handleSaveDebounce()
        } else {
            currentRectRef.current.set("stroke", stroke)
            fabricInstanceRef.current.requestRenderAll()
            // 缓存撤销
            handleAddUndo('handleMouseUpRect')
            // 更新标注
            handleSaveDebounce()
        }
        downPointRef.current = null
        upPointRef.current = null
        currentRectRef.current = null
    }

    // 绘制椭圆
    const currentEllipseRef = useRef()
    const handleMouseDownEllipse = ev => {
        const downPoint = ev.absolutePointer
        const ellipse = new fabric.Ellipse({
            left: downPoint.x,
            top: downPoint.y,
            rx: 0,
            ry: 0,
            stroke: strokeAlpha,
            strokeWidth: strokeWidthRef.current,
            ...fabricObjParams,
        })
        addDefaultControlVisibility(ellipse)
        currentEllipseRef.current = ellipse
        downPointRef.current = downPoint
        fabricInstanceRef.current.add(currentEllipseRef.current)
    }
    const handleMouseMoveEllipse = ev => {
        if (!downPointRef.current) return
        if (!currentEllipseRef.current) return
        const currentPoint = ev.absolutePointer
        const downPoint = downPointRef.current
        const rx = Math.abs(downPoint.x - currentPoint.x) / 2
        const ry = Math.abs(downPoint.y - currentPoint.y) / 2
        const top = currentPoint.y > downPoint.y ? downPoint.y : downPoint.y - ry * 2
        const left = currentPoint.x > downPoint.x ? downPoint.x : downPoint.x - rx * 2
        // 设置椭圆尺寸和所在位置
        currentEllipseRef.current.set({
            left: left,
            top: top,
            rx: rx,
            ry: ry,
        })
        fabricInstanceRef.current.requestRenderAll()
    }
    const handleMouseUpEllipse = ev => {
        if (!downPointRef.current) return
        if (!currentEllipseRef.current) return
        const upPoint = ev.absolutePointer
        const downPoint = downPointRef.current
        if (JSON.stringify(downPoint) === JSON.stringify(upPoint)) {
            fabricInstanceRef.current.remove(currentEllipseRef.current)
            // 更新标注
            handleSaveDebounce()
        } else {
            currentEllipseRef.current.set("stroke", stroke)
            fabricInstanceRef.current.requestRenderAll()
            // 缓存撤销
            handleAddUndo('handleMouseUpEllipse')
            // 更新标注
            handleSaveDebounce()
        }
        downPointRef.current = null
        upPointRef.current = null
        currentEllipseRef.current = null
    }

    // 箭头
    const currentArrowRef = useRef()
    function calcArrowAngle(x1, y1, x2, y2) {
        let angle = 0, x, y;
        x = (x2 - x1);
        y = (y2 - y1);
        if (x === 0) {
            angle = (y === 0) ? 0 : (y > 0) ? Math.PI / 2 : Math.PI * 3 / 2;
        } else if (y === 0) {
            angle = (x > 0) ? 0 : Math.PI;
        } else {
            angle = (x < 0) ? Math.atan(y / x) + Math.PI : (y < 0) ? Math.atan(y / x) + (2 * Math.PI) : Math.atan(y / x);
        }
        return (angle * 180 / Math.PI);
    }
    function arrowLinePath(x1, y1, x2, y2) {
        const dis = Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2))
        const arrowSize1 = Math.min(20, dis / 4)
        const arrowSize2 = arrowSize1 * 0.7
        const arrowSize3 = arrowSize1 * 0.3
        const point1 = [dis, 0]
        const point2 = [point1[0] - arrowSize1, point1[1] - arrowSize2]
        const point3 = [point1[0] - arrowSize1, point1[1] + arrowSize2]
        const point4 = [point1[0] - arrowSize1, point1[1] - arrowSize3]
        const point5 = [point1[0] - arrowSize1, point1[1] + arrowSize3]
        return `
            M 0 0
            L ${point4[0]} ${point4[1]}
            L ${point2[0]} ${point2[1]}
            L ${point1[0]} ${point1[1]}
            L ${point3[0]} ${point3[1]}
            L ${point5[0]} ${point5[1]}
            z
        `
    }
    const handleMouseDownArrow = ev => {
        const downPoint = ev.absolutePointer
        const x1 = downPoint.x
        const y1 = downPoint.y
        const x2 = downPoint.x
        const y2 = downPoint.y
        const path = new fabric.Path(arrowLinePath(x1, y1, x2, y2), {
            originX: 'left',
            originY: 'center',
            ...fabricObjParams,
            fill: stroke,
        })
        const angle = calcArrowAngle(x1, y1, x2, y2)
        path.set({
            angle: angle,
            left: downPoint.x,
            top: downPoint.y,
            isArrow: true
        })
        addDefaultControlVisibility(path)
        downPointRef.current = downPoint
        currentArrowRef.current = path
        fabricInstanceRef.current.add(path);
    }
    const handleMouseMoveArrow = ev => {
        if (!downPointRef.current) return
        if (!currentArrowRef.current) return
        const path = currentArrowRef.current
        const currentPoint = ev.absolutePointer
        const downPoint = downPointRef.current
        const x1 = downPoint.x
        const y1 = downPoint.y
        const x2 = currentPoint.x
        const y2 = currentPoint.y
        const updatedPath = new fabric.Path(arrowLinePath(x1, y1, x2, y2))
        const updateAngle = calcArrowAngle(x1, y1, x2, y2)
        path.set({
            path: updatedPath.path,
            width: updatedPath.width,
            height: updatedPath.height,
            pathOffset: updatedPath.pathOffset
        })
        path.set({ angle: updateAngle })
        path.setCoords()
        fabricInstanceRef.current.requestRenderAll()
    }
    const handleMouseUpArrow = ev => {
        if (!downPointRef.current) return
        if (!currentArrowRef.current) return
        const upPoint = ev.absolutePointer
        const downPoint = downPointRef.current

        if (JSON.stringify(downPoint) === JSON.stringify(upPoint)) {
            fabricInstanceRef.current.remove(currentArrowRef.current)
            // 更新标注
            handleSaveDebounce()
        } else {
            currentArrowRef.current.set("fill", stroke)
            fabricInstanceRef.current.requestRenderAll()
            // 缓存撤销
            handleAddUndo('handleMouseUpArrow')
            // 更新标注
            handleSaveDebounce()
        }
        downPointRef.current = null
        upPointRef.current = null
        currentArrowRef.current = null
    }

    // 输入框
    const currentTextRef = useRef()
    const normalizeTextObject = useCallback((textObject, { requestRender = true, adjustWidth = true } = {}) => {
        if (!textObject) return
        const instance = fabricInstanceRef.current
        if (!isFabricCanvasReady(instance)) return
        const canvasWidth = instance.width || props.width || 0
        const canvasHeight = instance.height || props.height || 0
        const scaleX = Math.abs(textObject.scaleX || 1)
        const minWidth = getMinTextboxWidth(textObject, fontSizeRef.current)
        const minScaledWidth = minWidth * scaleX
        const maxScaledWidth = Math.max(minScaledWidth, canvasWidth - TEXTBOX_EDGE_MARGIN * 2)
        if (textObject.type === 'textbox' && canvasWidth > 0) {
            const nextLeft = clampNumber(
                textObject.left ?? TEXTBOX_EDGE_MARGIN,
                TEXTBOX_EDGE_MARGIN,
                Math.max(TEXTBOX_EDGE_MARGIN, canvasWidth - minScaledWidth - TEXTBOX_EDGE_MARGIN)
            )
            const availableScaledWidth = Math.min(
                maxScaledWidth,
                Math.max(minScaledWidth, canvasWidth - nextLeft - TEXTBOX_EDGE_MARGIN)
            )
            const naturalScaledWidth = adjustWidth ?
                measureTextboxNaturalWidth(textObject) * scaleX :
                (textObject.getScaledWidth?.() || minScaledWidth)
            const nextScaledWidth = Math.min(Math.max(naturalScaledWidth, minScaledWidth), availableScaledWidth)
            const nextWidth = nextScaledWidth / scaleX
            textObject.set({ width: nextWidth, splitByGrapheme: true })
        }
        textObject.initDimensions?.()
        textObject.setCoords()
        const maxLeft = Math.max(TEXTBOX_EDGE_MARGIN, canvasWidth - textObject.getScaledWidth() - TEXTBOX_EDGE_MARGIN)
        const maxTop = Math.max(TEXTBOX_EDGE_MARGIN, canvasHeight - textObject.getScaledHeight() - TEXTBOX_EDGE_MARGIN)
        textObject.set({
            left: clampNumber(textObject.left ?? TEXTBOX_EDGE_MARGIN, TEXTBOX_EDGE_MARGIN, maxLeft),
            top: clampNumber(textObject.top ?? TEXTBOX_EDGE_MARGIN, TEXTBOX_EDGE_MARGIN, maxTop),
        })
        textObject.setCoords()
        if (requestRender) instance.requestRenderAll()
    }, [props.width, props.height])
    const createTextObject = useCallback((text = '', options = {}) => {
        const instance = fabricInstanceRef.current
        const canvasWidth = instance?.width || props.width || 0
        const estimatedLeft = options.left ?? TEXTBOX_EDGE_MARGIN
        const minWidth = getMinTextboxWidth({
            fontSize: fontSizeRef.current,
            padding: options.padding ?? 7,
        }, fontSizeRef.current)
        const availableWidth = Math.max(minWidth, canvasWidth - estimatedLeft - TEXTBOX_EDGE_MARGIN)
        const textObject = new fabric.Textbox(text, {
            padding: 7, // 设置输入框内边距
            fontSize: fontSizeRef.current,
            ...fabricObjParams,
            fill: stroke,
            perPixelTargetFind: false,
            splitByGrapheme: true,
            width: options.width || availableWidth,
            ...options,
        })
        normalizeTextObject(textObject, { requestRender: false })
        return textObject
    }, [normalizeTextObject, props.width])
    const prepareTextObject = useCallback((textObject, readonly) => {
        if (!textObject) return textObject
        if (!isFabricTextObject(textObject)) return textObject
        let nextTextObject = textObject
        if (textObject.type === 'i-text') {
            const rawObject = textObject.toObject()
            const text = rawObject.text || ''
            delete rawObject.type
            delete rawObject.text
            nextTextObject = createTextObject(text, {
                ...rawObject,
                selectable: !readonly,
            })
        } else {
            nextTextObject.set({
                ...fabricObjParams,
                fill: stroke,
                perPixelTargetFind: false,
                selectable: !readonly,
                splitByGrapheme: true,
            })
            normalizeTextObject(nextTextObject, { requestRender: false })
        }
        addEventToIText(nextTextObject)
        return nextTextObject
    }, [createTextObject, normalizeTextObject])
    const handleMouseDownText = (ev) => {
        const downPoint = ev.absolutePointer
        const textObject = createTextObject('', {
            left: downPoint.x, // 输入框离画布左侧距离
            top: downPoint.y, // 输入框离画布顶部距离
        })
        addDefaultControlVisibility(textObject)
        addEventToIText(textObject)
        fabricInstanceRef.current.add(textObject).setActiveObject(textObject)
        textObject.enterEditing()
    }
    const addEventToIText = (iText) => {
        iText.on('editing:entered', () => {
            currentTextRef.current = iText
            normalizeTextObject(iText, { adjustWidth: false })
        })
        iText.on('changed', () => {
            normalizeTextObject(iText)
            // 更新标注
            handleSaveDebounce()
        })
        iText.on('editing:exited', () => {
            if (!currentTextRef.current) return
            const obj = currentTextRef.current
            if (!obj.text?.trim()) {
                fabricInstanceRef.current.remove(obj)
                // 更新标注
                handleSaveDebounce()
            } else {
                normalizeTextObject(obj, { adjustWidth: false })
                // 缓存撤销
                handleAddUndo('editing:exited')
                // 更新标注
                handleSaveDebounce()
            }
            currentTextRef.current = null
        })
    }

    // 图片
    const currentImageRef = useRef()
    const handleAddImage = async (url) => {
        if (!url || props.readonly || !fabricInstanceRef.current) return
        try {
            await new Promise((resolve, reject) => {
                fabric.Image.fromURL(url, (image) => {
                    if (!image) {
                        reject(new Error('create-image-object-failed'))
                        return
                    }
                    if (!isFabricCanvasReady(fabricInstanceRef.current)) {
                        resolve(false)
                        return
                    }
                    const canvasWidth = fabricInstanceRef.current.width || props.width
                    const canvasHeight = fabricInstanceRef.current.height || props.height
                    const maxWidth = canvasWidth / 3
                    const maxHeight = canvasHeight / 3
                    const originalWidth = image.width || maxWidth
                    const originalHeight = image.height || maxHeight
                    const scale = Math.min(maxWidth / originalWidth, maxHeight / originalHeight, 1)
                    const left = canvasWidth / 20
                    const top = canvasHeight / 20
                    image.set({
                        left: left,
                        top: top,
                        scaleX: scale,
                        scaleY: scale,
                        selectable: true,
                        perPixelTargetFind: true,
                        ...fabricObjParams,
                    })
                    image.setControlsVisibility({
                        mt: false, // 隐藏上边中心点
                        mb: false, // 隐藏下边中心点
                        ml: false, // 隐藏左边中心点
                        mr: false, // 隐藏右边中心点
                        mtr: false // 隐藏旋转点
                    })
                    currentImageRef.current = image
                    fabricInstanceRef.current.add(image).setActiveObject(image)
                    fabricInstanceRef.current.requestRenderAll()
                    // 缓存撤销
                    handleAddUndo('handleAddImage')
                    // 更新标注
                    handleSaveDebounce()
                    resolve(true)
                }, { crossOrigin: 'anonymous' })
            })
        } catch (error) {
            message.error('图片标注上传失败')
        }
    }

    const handleMouseDown = (ev) => {
        const target = ev.target
        if (target) return
        if (modeRef.current == "rect") {
            handleMouseDownRect(ev)
        } else if (modeRef.current == "ellipse") {
            handleMouseDownEllipse(ev)
        } else if (modeRef.current == "text") {
            handleMouseDownText(ev)
        } else if (modeRef.current == "arrow") {
            handleMouseDownArrow(ev)
        } else if (modeRef.current == "image") {
            return
        }
        const objs = fabricInstanceRef.current.getObjects()
        objs.forEach(obj => obj.selectable = false)
        fabricInstanceRef.current.renderAll()
    }
    const handleMouseMove = (ev) => {
        if (modeRef.current == "rect") {
            handleMouseMoveRect(ev)
        } else if (modeRef.current == "ellipse") {
            handleMouseMoveEllipse(ev)
        } else if (modeRef.current == "arrow") {
            handleMouseMoveArrow(ev)
        }
    }
    const handleMouseUp = (ev) => {
        if (modeRef.current == "rect") {
            handleMouseUpRect(ev)
        } else if (modeRef.current == "ellipse") {
            handleMouseUpEllipse(ev)
        } else if (modeRef.current == "arrow") {
            handleMouseUpArrow(ev)
        }
        const objs = fabricInstanceRef.current.getObjects()
        objs.forEach(obj => { obj.selectable = true; obj.setCoords(); })
        fabricInstanceRef.current.renderAll()
        // 更新标注
        handleSaveDebounce()
    }
    const handlePathCreated = ({ path }) => {
        path.set({ ...fabricObjParams })
        addDefaultControlVisibility(path)
        // 缓存撤销
        handleAddUndo('handlePathCreated')
        // 更新标注
        handleSaveDebounce()
    }

    const getTags = () => {
        if (!fabricInstanceRef.current) return []
        const canvasWidth = fabricInstanceRef.current.width
        const canvasHeight = fabricInstanceRef.current.height
        const objs = fabricInstanceRef.current.getObjects()
        const tags = objs.filter(obj => {
            if (isFabricTextObject(obj)) return !!obj.text?.trim()
            else return true
        }).map(obj => {
            return {
                type: obj.type,
                json: JSON.stringify(obj.toJSON(['isArrow'])),
                canvasWidth: canvasWidth,
                canvasHeight: canvasHeight,
            }
        })
        return tags
    }
    const handleSaveDebounce = useCallback(_.debounce(() => {
        if (!isMountedRef.current) return
        const tags = getTags()
        props.onLabelChange(tags)
    }, 300), [])
    const loadPureText = (instance, readonly, data) => {
        if (data.length == 0) return Promise.resolve(true)
        return new Promise(resolve => {
            if (!isFabricCanvasReady(instance)) {
                resolve(false)
                return
            }
            const canvasWidth = instance.width
            const canvasHeight = instance.height
            for (let item of data) {
                const textObject = createTextObject(item.comment || '', {
                    left: canvasWidth - Math.min(canvasWidth * 0.4, 260) - TEXTBOX_EDGE_MARGIN,
                    top: canvasHeight - (fontSizeRef.current || 0) * 2 - TEXTBOX_EDGE_MARGIN,
                    selectable: !readonly
                })
                addDefaultControlVisibility(textObject)
                addEventToIText(textObject)
                normalizeTextObject(textObject, { requestRender: false })
                instance.add(textObject)
            }
            if (!isFabricCanvasReady(instance)) {
                resolve(false)
                return
            }
            instance.renderAll()
            resolve(true)
        })
    }
    const loadData = (instance, readonly, data) => {
        if (data.length == 0) return Promise.resolve(true)
        return new Promise(resolve => {
            const canvasWidth = data[0].canvasWidth
            const canvasHeight = data[0].canvasHeight
            const width = instance.width
            const height = instance.height
            const canvasScaleX = width / canvasWidth
            const canvasScaleY = height / canvasHeight
            fabric.util.enlivenObjects(data.filter(item => item.json)
                .map(item => JSON.parse(item.json))
                .filter(obj => {
                    if (isFabricTextObject(obj)) return !!obj.text?.trim()
                    else return true
                }), sourceObjs => {
                if (!isFabricCanvasReady(instance)) {
                    resolve(false)
                    return
                }
                const objs = sourceObjs.map(item => prepareTextObject(item, readonly))
                objs.forEach(item => {
                    addDefaultControlVisibility(item)
                    if (item.type == 'textbox') {
                        item.set({
                            selectable: !readonly,
                            splitByGrapheme: true,
                        })
                        normalizeTextObject(item, { requestRender: false })
                    } else if (item.type == 'image') {
                        item.set({
                            selectable: !readonly,
                            perPixelTargetFind: true,
                            ...fabricObjParams,
                        })
                    } else if (item.type == 'path') {
                        if (item.get("isArrow")) {
                            item.set({
                                ...fabricObjParams,
                                fill: stroke,
                                selectable: !readonly
                            })
                        } else {
                            item.set({
                                ...fabricObjParams,
                                selectable: !readonly
                            })
                        }
                    } else {
                        item.set({
                            ...fabricObjParams,
                            selectable: !readonly
                        })
                    }
                })
                objs.forEach(item => {
                    if (item.type == 'path') {
                        item.scale(item.scaleX * canvasScaleX)
                        item.set({
                            left: formartNumber(item.left * canvasScaleX),
                            top: formartNumber(item.top * canvasScaleY),
                        })
                    } else if (item.type == "ellipse") {
                        item.set({
                            left: formartNumber(item.left * canvasScaleX),
                            top: formartNumber(item.top * canvasScaleY),
                            rx: formartNumber(item.rx * canvasScaleX),
                            ry: formartNumber(item.ry * canvasScaleY),
                        })
                    } else if (item.type == "textbox") {
                        item.set({fontSize: item.fontSize * canvasScaleX})
                        item.set({
                            width: formartNumber(item.width * canvasScaleX),
                            height: formartNumber(item.height * canvasScaleX),
                            left: formartNumber(item.left * canvasScaleX),
                            top: formartNumber(item.top * canvasScaleY),
                        })
                        normalizeTextObject(item, { requestRender: false })
                    } else if (item.type == "image") {
                        item.set({
                            left: formartNumber(item.left * canvasScaleX),
                            top: formartNumber(item.top * canvasScaleY),
                            scaleX: formartNumber((item.scaleX || 1) * canvasScaleX),
                            scaleY: formartNumber((item.scaleY || 1) * canvasScaleY),
                        })
                    } else {
                        item.set({
                            width: formartNumber(item.width * canvasScaleX),
                            height: formartNumber(item.height * canvasScaleX),
                            left: formartNumber(item.left * canvasScaleX),
                            top: formartNumber(item.top * canvasScaleY),
                        })
                    }
                    item.setCoords()
                })
                if (!isFabricCanvasReady(instance)) {
                    resolve(false)
                    return
                }
                instance.add(...objs)
                instance.renderAll()
                resolve(true)
            })
        })
    }
    // 快捷键
    const undoListRef = useRef([])
    const [undoList, setUndoList] = useState([])
    const handleKeyDown = (e) => {
        if (props.readonly) return
        // 文本输入沿用浏览器原生快捷键，避免画布的全局监听拦截评论编辑。
        if (isEditableKeyboardTarget(e)) return
        if (!fabricInstanceRef.current) return
        const commandKey = e.ctrlKey || e.metaKey
        if (commandKey && e.key.toLocaleLowerCase() == "a") { // 禁止浏览器选中整页内容
            // 阻止事件冒泡
            if (e.stopPropagation) e.stopPropagation();
            else e.cancelBubble = true;
            // 取消事件默认行为
            if(e.preventDefault) e.preventDefault();
            else e.returnValue = false;
        } else if (commandKey && e.key.toLocaleLowerCase() == "z") { // ctrl/command+z 撤销
            handleUndo()
        } else if (e.key == "Delete") { // 删除
            const editingTextObject = currentTextRef.current
            if (editingTextObject?.isEditing) return
            const obj = fabricInstanceRef.current.getActiveObject()
            if (obj) {
                if (obj.type == "activeSelection") {
                    fabricInstanceRef.current.clear()
                } else {
                    fabricInstanceRef.current.FX_DURATION = 100 // 设置动画时长，默认500毫秒
                    fabricInstanceRef.current.fxRemove(obj, {
                        onComplete: () => {
                            // 缓存撤销
                            handleAddUndo('handleKeyDownDelete')
                            // 更新标注
                            handleSaveDebounce()
                        }
                    })
                }
            }
        }
    }
    const handleUndo = () => {
        if (!fabricInstanceRef.current) return
        // 清除当前正在操作的数据
        downPointRef.current = null
        upPointRef.current = null
        currentRectRef.current = null
        currentArrowRef.current = null
        currentEllipseRef.current = null
        currentTextRef.current = null
        
        undoListRef.current.pop() // 丢弃当前
        const tags = undoListRef.current.at(-1) // 取前一个场景
        setUndoList(undoListRef.current)
        if (tags) {
            fabricInstanceRef.current.clear()
            loadData(fabricInstanceRef.current, props.readonly, tags).then(() => {
                if (!isMountedRef.current) return
                // 更新标注
                handleSaveDebounce()
            })
        } else {
            const objs = fabricInstanceRef.current.getObjects()
            const obj = objs.pop()
            if (obj) {
                fabricInstanceRef.current.FX_DURATION = 100 // 设置动画时长，默认500毫秒
                fabricInstanceRef.current.fxRemove(obj, {
                    onComplete: () => {
                        // 更新标注
                        handleSaveDebounce()
                    }
                })
            }
        }
    }
    const handleReset = () => {
        if (!fabricInstanceRef.current) return
        undoListRef.current = []
        setUndoList(undoListRef.current)
        fabricInstanceRef.current.clear()
        // 更新标注
        handleSaveDebounce()
    }
    // const handleObjectModified = () => {
    //     // 缓存撤销
    //     handleAddUndo('handleObjectModified')
    // }
    const handleObjectMove = useCallback(_.debounce((event) => {
        const target = event?.target
        if (target?.type === 'textbox') {
            normalizeTextObject(target)
        }
        // 缓存撤销
        handleAddUndo('handleObjectMove')
        // 更新标注
        handleSaveDebounce()
    }, 200), [])
    const handleObjectScale = useCallback(_.debounce((event) => {
        const target = event?.target
        if (target?.type === 'textbox') {
            normalizeTextObject(target, { adjustWidth: false })
        }
        // 缓存撤销
        handleAddUndo('handleObjectScale')
        // 更新标注
        handleSaveDebounce()
    }, 200), [])
    const handleAddUndo = (from) => {
        if (!isMountedRef.current) return
        console.log('AddUndo', from)
        const tags = getTags()
        undoListRef.current.push(tags)
        setUndoList(undoListRef.current)
    }

    const canvasRef = useRef()
    const [fabricInstance, setFabricInstance] = useState()
    const fabricInstanceRef = useRef()
    useEffect(() => {
        isMountedRef.current = true
        const width = props.width
        const heigth = props.height
        const instance = new fabric.Canvas(canvasRef.current, {
            width: width,
            height: heigth,
            fireRightClick: false, // 启用右键
            stopContextMenu: true, // 禁用右键菜单
            isDrawingMode: false, // 禁用绘图模式
            selectionBorderColor: "transparent", // rgba(255, 255, 255, 0.3)
            selectionColor: "transparent", // rgba(100, 100, 255, 0.3)
        })
        instance.freeDrawingBrush.width = strokeWidthRef.current
        instance.freeDrawingBrush.color = stroke

        const pureText = (props.labels??[]).filter(item => "comment" in item)
        const otherData = (props.labels??[]).filter(item => "canvasWidth" in item)
        loadData(instance, props.readonly, otherData)
            .then(() => loadPureText(instance, props.readonly, pureText))
            .then(res => {
                if (!isMountedRef.current || !res) return
                // 缓存撤销
                handleAddUndo('loadData')
            })

        if (!props.readonly) {
            instance.on('mouse:down', handleMouseDown)
            instance.on('mouse:move', handleMouseMove)
            instance.on('mouse:up', handleMouseUp)
            instance.on('path:created', handlePathCreated)
            // instance.on('after:render', handleSaveDebounce)
            // instance.on('object:modified', handleObjectModified)
            instance.on('object:moving', handleObjectMove) // 移动
            instance.on('object:scaling', handleObjectScale) // 缩放
        }

        fabricInstanceRef.current = instance
        setFabricInstance(instance)

        document.addEventListener("keydown", handleKeyDown, true)
        return () => {
            isMountedRef.current = false
            document.removeEventListener("keydown", handleKeyDown, true)
            handleSaveDebounce.cancel()
            handleObjectMove.cancel()
            handleObjectScale.cancel()
            const currentInstance = fabricInstanceRef.current
            fabricInstanceRef.current = null
            currentInstance?.dispose()
        }
    }, [])

    useEffect(() => {
        if (!fabricInstance) return
        if (!props.mode) {
            fabricInstance.discardActiveObject()
            fabricInstance.renderAll()
        }
        if (props.mode == 'brush') {
            fabricInstance.isDrawingMode = true
        } else {
            fabricInstance.isDrawingMode = false
        }
        if (props.mode == "rect") {
            fabricInstance.defaultCursor = "crosshair"
            fabricInstance.hoverCursor = "pointer"
        } else if (props.mode == "ellipse") {
            fabricInstance.defaultCursor = "crosshair"
            fabricInstance.hoverCursor = "pointer"
        } else if (props.mode == "brush") {
            fabricInstance.defaultCursor = "crosshair"
            fabricInstance.hoverCursor = "crosshair"
            fabricInstance.freeDrawingCursor = "crosshair"
        } else if (props.mode == "arrow") {
            fabricInstance.defaultCursor = "crosshair"
            fabricInstance.hoverCursor = "pointer"
        } else if (props.mode == "text") {
            fabricInstance.defaultCursor = "text"
            fabricInstance.hoverCursor = "pointer"
        } else if (props.mode == "image") {
            fabricInstance.defaultCursor = "copy"
            fabricInstance.hoverCursor = "pointer"
        } else {
            fabricInstance.defaultCursor = "pointer"
            fabricInstance.hoverCursor = "pointer"
        }
    }, [props.mode, fabricInstance])

    useEffect(() => {
        if (!fabricInstance) return
        const width = props.width
        const heigth = props.height

        const canvasScaleX = width / fabricInstance.width
        const canvasScaleY = heigth / fabricInstance.height

        fabricInstance.setDimensions({ width: width, height: heigth })

        const objs = fabricInstance.getObjects()
        objs.forEach(item => {
            if (item.type == 'path') {
                item.scale(item.scaleX * canvasScaleX)
                item.set({
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
            } else if (item.type == "ellipse") {
                item.set({
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                    rx: formartNumber(item.rx * canvasScaleX),
                    ry: formartNumber(item.ry * canvasScaleY),
                })
            } else if (item.type == "textbox") {
                item.set({fontSize: item.fontSize * canvasScaleX})
                item.set({
                    width: formartNumber(item.width * canvasScaleX),
                    height: formartNumber(item.height * canvasScaleX),
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
                normalizeTextObject(item, { requestRender: false })
            } else if (item.type == "image") {
                item.set({
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                    scaleX: formartNumber((item.scaleX || 1) * canvasScaleX),
                    scaleY: formartNumber((item.scaleY || 1) * canvasScaleY),
                })
            } else {
                item.set({
                    width: formartNumber(item.width * canvasScaleX),
                    height: formartNumber(item.height * canvasScaleX),
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
            }
            item.setCoords()
        })
        fabricInstance.renderAll()
    }, [fabricInstance, props.width, props.height])

    useEffect(() => {
        if (!fabricInstance) return
        return () => {
            fabricInstance.off('mouse:down')
            fabricInstance.off('mouse:up')
        }
    }, [fabricInstance])

    return (
        <div
            className={`${styles.LabelBox} ${!props.mode && styles.pointerEventNone}`}
            style={props.style}
            onWheel={props.onWheel}
        >
            <canvas ref={canvasRef} />
        </div>
    )
})

export default LabelBox2

const TEXTBOX_EDGE_MARGIN = 8
const TEXTBOX_MIN_DISPLAY_WIDTH = 32

function formartNumber(number, digits = 2) {
    return Number(number.toFixed(digits))
}
function isEditableKeyboardTarget(event) {
    return isEditableElement(event?.target) || isEditableElement(document.activeElement)
}
function isEditableElement(element) {
    if (!(element instanceof HTMLElement)) return false
    if (element.isContentEditable) return true
    return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA'
}
function clampNumber(value, min, max) {
    if (min > max) return min
    return Math.min(Math.max(value, min), max)
}
function getMinTextboxWidth(textObject, fallbackFontSize = 16) {
    const padding = textObject?.padding || 0
    const fontSize = textObject?.fontSize || fallbackFontSize || 16
    return Math.max(TEXTBOX_MIN_DISPLAY_WIDTH, fontSize + padding * 2)
}
function measureTextboxNaturalWidth(textObject) {
    if (!textObject) return TEXTBOX_MIN_DISPLAY_WIDTH
    const measureText = new fabric.Text(textObject.text || '', {
        fontSize: textObject.fontSize,
        fontFamily: textObject.fontFamily,
        fontWeight: textObject.fontWeight,
        fontStyle: textObject.fontStyle,
        lineHeight: textObject.lineHeight,
        charSpacing: textObject.charSpacing,
        underline: textObject.underline,
        linethrough: textObject.linethrough,
        overline: textObject.overline,
        splitByGrapheme: true,
    })
    return measureText.getScaledWidth() + (textObject.padding || 0) * 2
}
function isFabricTextObject(obj) {
    const type = obj?.type
    return type === 'i-text' || type === 'textbox'
}
function isFabricCanvasReady(instance) {
    return !!(instance && instance.lowerCanvasEl && instance.contextContainer)
}
function addDefaultControlVisibility(obj) {
    obj.setControlsVisibility({
        mt: false, // 隐藏上边中心点
        mb: false, // 隐藏下边中心点
        ml: false, // 隐藏左边中心点
        mr: false, // 隐藏右边中心点
        mtr: false // 隐藏旋转点
    })
}
